// src/components/TradeList.js - 수수료율 수정된 버전

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useWallet } from '../hooks/useWallet';
import { useContract } from '../hooks/useContract';
import { getTradeFeePercent, feePercentToPercentage } from '../utils/contract';
import PaymentProofModal from './PaymentProofModal';
import './TradeList.css';

const TradeList = () => {
  const { account } = useWallet();
  const { userTrades, loadUserTrades, confirmPayment, cancelTrade, loading, error } = useContract();
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showPaymentProof, setShowPaymentProof] = useState(false);
  const [paymentProofTrade, setPaymentProofTrade] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [tradeFeeRates, setTradeFeeRates] = useState({}); // 거래별 수수료율 캐시

  // 거래 목록 로드
  useEffect(() => {
    console.log('TradeList useEffect 실행, account:', account);
    if (account) {
      console.log('거래 목록 로드 시작');
      loadUserTrades(account);
    }
  }, [account, loadUserTrades]);

  // 거래별 수수료율 로드
  useEffect(() => {
    const loadTradeFeeRates = async () => {
      if (userTrades.length === 0) return;

      const newFeeRates = {};
      
      for (const trade of userTrades) {
        try {
          // 이미 trade 객체에 feePercent가 있다면 사용, 없다면 조회
          if (trade.feePercent !== undefined) {
            newFeeRates[trade.tradeId] = trade.feePercent;
          } else {
            const feePercent = await getTradeFeePercent(trade.tradeId);
            newFeeRates[trade.tradeId] = feePercent;
          }
        } catch (error) {
          console.error(`거래 ${trade.tradeId} 수수료율 로드 실패:`, error);
          // 실패 시 기본값 사용 (100 = 1%)
          newFeeRates[trade.tradeId] = 100;
        }
      }
      
      setTradeFeeRates(newFeeRates);
    };

    loadTradeFeeRates();
  }, [userTrades]);

  // 거래의 수수료율 가져오기
  const getTradeFeeRate = useCallback((trade) => {
    // 1. trade 객체에 feePercent가 있으면 사용
    if (trade.feePercent !== undefined) {
      return trade.feePercent;
    }
    
    // 2. 캐시된 수수료율 사용
    if (tradeFeeRates[trade.tradeId] !== undefined) {
      return tradeFeeRates[trade.tradeId];
    }
    
    // 3. 기본값 반환 (100 = 1%)
    return 100;
  }, [tradeFeeRates]);

  // 거래 상태별 스타일과 설명
  const getStatusInfo = useCallback((status) => {
    // BigInt를 Number로 변환
    const statusNum = Number(status);
    
    const statusConfig = {
      0: { 
        text: '판매자가 구매자의 송금을 기다리고 있습니다.', 
        class: 'status-created',
        description: '구매자의 송금 대기 중',
        icon: '⏳'
      },
      1: { 
        text: '송금증명 제출됨. 판매자의 입금 확인 대기 중', 
        class: 'status-submitted',
        description: '송금증명 제출됨. 판매자의 입금 확인 대기 중',
        icon: '📤'
      },
      2: { 
        text: '완료됨', 
        class: 'status-completed',
        description: '거래가 성공적으로 완료됨',
        icon: '✅'
      },
      3: { 
        text: '취소됨', 
        class: 'status-cancelled',
        description: '거래가 취소됨',
        icon: '❌'
      }
    };

    const config = statusConfig[statusNum] || { 
      text: '알 수 없음', 
      class: 'status-unknown',
      description: '상태를 확인할 수 없음',
      icon: '❓'
    };

    return config;
  }, []);

  const getStatusBadge = useCallback((status) => {
    const statusInfo = getStatusInfo(status);
    return (
      <span className={`status-badge ${statusInfo.class}`} title={statusInfo.description}>
        {statusInfo.icon} {statusInfo.text}
      </span>
    );
  }, [getStatusInfo]);

  // 거래 역할 확인
  const getUserRole = useCallback((trade) => {
    if (!account) return '';
    if (trade.seller.toLowerCase() === account.toLowerCase()) return 'seller';
    if (trade.buyer.toLowerCase() === account.toLowerCase()) return 'buyer';
    return '';
  }, [account]);

  // 거래 만료 확인
  const isTradeExpired = useCallback((trade) => {
    return new Date(trade.expiresAt) < new Date();
  }, []);

  // 다음 액션 결정
  const getNextAction = useCallback((trade) => {
    const role = getUserRole(trade);
    const expired = isTradeExpired(trade);
    
    // BigInt를 Number로 변환
    const status = Number(trade.status);

    if (expired && status < 2) {
      return role === 'seller' ? 'cancel' : 'expired';
    }

    switch (status) {
      case 0: // 생성됨
        return role === 'buyer' ? 'submit_proof' : 'wait_payment';
      case 1: // 송금증명 제출됨
        return role === 'seller' ? 'confirm_payment' : 'wait_confirmation';
      case 2: // 완료됨
        return 'completed';
      case 3: // 취소됨
        return 'cancelled';
      default:
        return 'unknown';
    }
  }, [getUserRole, isTradeExpired]);

  // 필터링된 거래 목록
  const filteredTrades = useMemo(() => {
    return userTrades.filter(trade => {
      if (filter === 'all') return true;
      return getUserRole(trade) === filter;
    }).sort((a, b) => {
      // 상태별 우선순위: 액션 필요한 것 먼저, 그 다음 최신순
      const aAction = getNextAction(a);
      const bAction = getNextAction(b);
      const actionPriority = {
        'submit_proof': 1,
        'confirm_payment': 2,
        'cancel': 3,
        'wait_payment': 4,
        'wait_confirmation': 5,
        'completed': 6,
        'cancelled': 7,
        'expired': 8
      };
      
      const aPriority = actionPriority[aAction] || 9;
      const bPriority = actionPriority[bAction] || 9;
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      // 같은 우선순위면 최신순
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [userTrades, filter, getUserRole, getNextAction]);

  // 새로고침 핸들러
  const handleRefresh = useCallback(() => {
    console.log('수동 새로고침 시작, account:', account);
    if (account) {
      loadUserTrades(account);
    }
  }, [account, loadUserTrades]);

  // 송금 증명 제출 핸들러
  const handleSubmitPaymentProof = (trade) => {
    setPaymentProofTrade(trade);
    setShowPaymentProof(true);
  };

  // 입금 확인 핸들러
  const handleConfirmPayment = async (tradeId) => {
    const confirmMessage = `입금을 확인하셨습니까?\n\n⚠️ 주의: 확인 후에는 취소할 수 없으며, WLC가 즉시 구매자에게 전송됩니다.\n\n실제로 계좌에 정확한 금액이 입금되었는지 다시 한 번 확인해주세요.`;
    
    if (window.confirm(confirmMessage)) {
      setActionLoading(tradeId);
      try {
        await confirmPayment(tradeId);
        alert('🎉 거래가 성공적으로 승인되었습니다!\n\nWLC가 구매자에게 전송되었습니다.');
        loadUserTrades(account);
      } catch (error) {
        console.error('입금 확인 실패:', error);
        let errorMessage = '입금 확인에 실패했습니다.';
        
        if (error.message.includes('User denied transaction')) {
          errorMessage = '트랜잭션이 취소되었습니다.';
        } else if (error.message.includes('insufficient funds')) {
          errorMessage = '가스비가 부족합니다.';
        }
        
        alert(`❌ ${errorMessage}\n\n${error.message}`);
      } finally {
        setActionLoading(null);
      }
    }
  };

  // 거래 취소 핸들러
  const handleCancelTrade = async (tradeId, reason = "사용자 요청") => {
    const confirmMessage = `거래를 취소하시겠습니까?\n\n취소 사유: ${reason}\n\n락업된 WLC가 판매자에게 반환됩니다.`;
    
    if (window.confirm(confirmMessage)) {
      setActionLoading(tradeId);
      try {
        await cancelTrade(tradeId, reason);
        alert('거래가 취소되었습니다.\n\nWLC가 판매자에게 반환되었습니다.');
        loadUserTrades(account);
      } catch (error) {
        console.error('거래 취소 실패:', error);
        alert(`거래 취소에 실패했습니다:\n${error.message}`);
      } finally {
        setActionLoading(null);
      }
    }
  };

  // 액션 버튼 렌더링
  const renderActionButton = (trade) => {
    const action = getNextAction(trade);
    const isLoading = actionLoading === trade.tradeId;

    switch (action) {
      case 'submit_proof':
        return (
          <button 
            className="action-btn submit-proof"
            onClick={(e) => {
              e.stopPropagation();
              handleSubmitPaymentProof(trade);
            }}
            disabled={isLoading}
          >
            📤 송금 증명서 제출하기
          </button>
        );

      case 'confirm_payment':
        return (
          <button 
            className="action-btn confirm-payment"
            onClick={(e) => {
              e.stopPropagation();
              handleConfirmPayment(trade.tradeId);
            }}
            disabled={isLoading}
          >
            {isLoading ? '처리 중...' : '✅ 입금 확인'}
          </button>
        );

      case 'cancel':
        return (
          <button 
            className="action-btn cancel-trade"
            onClick={(e) => {
              e.stopPropagation();
              handleCancelTrade(trade.tradeId, "거래 만료");
            }}
            disabled={isLoading}
          >
            {isLoading ? '처리 중...' : '❌ 거래 취소'}
          </button>
        );

      case 'wait_payment':
        return (
          <div className="status-info">
            ⏳ 구매자 송금 대기 중
          </div>
        );

      case 'wait_confirmation':
        return (
          <div className="status-info">
            ⏳ 판매자 확인 대기 중
          </div>
        );

      case 'completed':
        return (
          <div className="status-info success">
            ✅ 거래 완료
          </div>
        );

      case 'cancelled':
        return (
          <div className="status-info cancelled">
            ❌ 거래 취소됨
          </div>
        );

      case 'expired':
        return (
          <div className="status-info expired">
            ⏰ 거래 만료됨
          </div>
        );

      default:
        return null;
    }
  };

  // 거래 상세 모달 컴포넌트
  const TradeDetailModal = useMemo(() => {
    if (!selectedTrade) return null;

    const role = getUserRole(selectedTrade);
    const isExpired = isTradeExpired(selectedTrade);
    const statusInfo = getStatusInfo(selectedTrade.status);
    
    // 해당 거래의 수수료율 가져오기
    const tradeFeePercent = getTradeFeeRate(selectedTrade);
    const tradeFeePercentage = feePercentToPercentage(tradeFeePercent);

    return (
      <div className="modal-overlay" onClick={() => setSelectedTrade(null)}>
        <div className="modal-content trade-detail-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3>📋 거래 상세 정보</h3>
            <button className="close-button" onClick={() => setSelectedTrade(null)}>×</button>
          </div>
          
          <div className="modal-body">
            {/* 상태 요약 */}
            <div className="status-summary">
              <div className="status-main">
                {statusInfo.icon} <span className="status-text">{statusInfo.text}</span>
              </div>
              <div className="status-description">{statusInfo.description}</div>
              {isExpired && Number(selectedTrade.status) < 2 && (
                <div className="expired-warning">
                  ⚠️ 이 거래는 만료되었습니다
                </div>
              )}
            </div>

            {/* 기본 정보 */}
            <div className="detail-section">
              <h4>📊 기본 정보</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="label">거래 ID:</span>
                  <span className="value"># {selectedTrade.tradeId}</span>
                </div>
                <div className="detail-item">
                  <span className="label">내 역할:</span>
                  <span className={`value role-badge ${role}`}>
                    {role === 'seller' ? '🔴 판매자' : '🔵 구매자'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">생성일:</span>
                  <span className="value">{selectedTrade.createdAt.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <span className="label">만료일:</span>
                  <span className="value">
                    {selectedTrade.expiresAt.toLocaleString()}
                    {isExpired && <span className="expired-text"> (만료됨)</span>}
                  </span>
                </div>
              </div>
            </div>

            {/* 거래 정보 */}
            <div className="detail-section">
              <h4>💱 거래 정보</h4>
              <div className="trade-amounts">
                <div className="amount-box">
                  <div className="amount-label">WLC 수량</div>
                  <div className="amount-value">{selectedTrade.wlcAmount} WLC</div>
                </div>
                <div className="amount-arrow">→</div>
                <div className="amount-box">
                  <div className="amount-label">원화 금액</div>
                  <div className="amount-value">{parseInt(selectedTrade.krwAmount).toLocaleString()} 원</div>
                </div>
              </div>
              <div className="price-info">
                <div className="price-item">
                  <span>개당 가격:</span>
                  <span>{Math.round(selectedTrade.krwAmount / selectedTrade.wlcAmount).toLocaleString()} 원/WLC</span>
                </div>
                <div className="price-item">
                  <span>수수료 ({tradeFeePercentage}):</span>
                  <span>{(selectedTrade.wlcAmount * tradeFeePercent / 10000).toFixed(3)} WLC</span>
                </div>
                <div className="price-item highlight">
                  <span>실제 받을 양:</span>
                  <span>
                    {role === 'buyer' 
                      ? `${(selectedTrade.wlcAmount * (1 - tradeFeePercent / 10000)).toFixed(3)} WLC`
                      : `${parseInt(selectedTrade.krwAmount).toLocaleString()} 원`
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* 참여자 정보 */}
            <div className="detail-section">
              <h4>👥 참여자 정보</h4>
              <div className="participants">
                <div className="participant">
                  <div className="participant-role">🔴 판매자</div>
                  <div className="participant-address">
                    {selectedTrade.seller}
                    {role === 'seller' && <span className="me-badge">나</span>}
                  </div>
                </div>
                <div className="participant">
                  <div className="participant-role">🔵 구매자</div>
                  <div className="participant-address">
                    {selectedTrade.buyer}
                    {role === 'buyer' && <span className="me-badge">나</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* 송금 증명 정보 */}
            {selectedTrade.paymentProofHash && (
              <div className="detail-section">
                <h4>📤 송금 증명</h4>
                <div className="proof-info">
                  <div className="proof-item">
                    <span className="label">증명 해시:</span>
                    <span className="value hash">{selectedTrade.paymentProofHash}</span>
                  </div>
                  <div className="proof-item">
                    <span className="label">제출 상태:</span>
                    <span className="value">✅ 제출 완료</span>
                  </div>
                </div>
                
                {/* 저장된 송금 정보 표시 */}
                {(() => {
                  const storedInfo = localStorage.getItem(`transfer_info_${selectedTrade.tradeId}`);
                  if (storedInfo) {
                    const info = JSON.parse(storedInfo);
                    return (
                      <div className="stored-info">
                        <h5>📝 제출된 송금 정보</h5>
                        <div className="info-grid">
                          <div className="info-item">
                            <span>송금 시간:</span>
                            <span>{new Date(info.transferTime).toLocaleString()}</span>
                          </div>
                          <div className="info-item">
                            <span>입금자명:</span>
                            <span>{info.senderName}</span>
                          </div>
                          <div className="info-item">
                            <span>은행명:</span>
                            <span>{info.bankName}</span>
                          </div>
                          {info.memo && (
                            <div className="info-item">
                              <span>메모:</span>
                              <span>{info.memo}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}

            {/* 액션 버튼들 */}
            <div className="detail-section">
              <h4>🎯 액션</h4>
              <div className="modal-actions">
                {/* 구매자: 송금 증명 제출 */}
                {role === 'buyer' && Number(selectedTrade.status) === 0 && !isExpired && (
                  <button 
                    className="modal-action-btn submit-proof"
                    onClick={() => {
                      setSelectedTrade(null);
                      handleSubmitPaymentProof(selectedTrade);
                    }}
                  >
                    📤 송금 증명 제출
                  </button>
                )}

                {/* 판매자: 입금 확인 */}
                {role === 'seller' && Number(selectedTrade.status) === 1 && !isExpired && (
                  <button 
                    className="modal-action-btn confirm-payment"
                    onClick={() => {
                      setSelectedTrade(null);
                      handleConfirmPayment(selectedTrade.tradeId);
                    }}
                  >
                    ✅ 입금 확인
                  </button>
                )}

                {/* 만료된 거래 취소 (판매자만) */}
                {role === 'seller' && isExpired && Number(selectedTrade.status) < 2 && (
                  <button 
                    className="modal-action-btn cancel-trade"
                    onClick={() => {
                      setSelectedTrade(null);
                      handleCancelTrade(selectedTrade.tradeId, "거래 만료");
                    }}
                  >
                    ❌ 거래 취소
                  </button>
                )}

                {/* 상태별 안내 메시지 */}
                {role === 'buyer' && Number(selectedTrade.status) === 1 && (
                  <div className="action-message">
                    ⏳ 송금 증명을 제출했습니다. 판매자가 입금을 확인할 때까지 기다려주세요.
                  </div>
                )}

                {role === 'seller' && Number(selectedTrade.status) === 0 && (
                  <div className="action-message">
                    ⏳ 구매자가 송금을 완료하고 증명을 제출할 때까지 기다려주세요.
                  </div>
                )}

                {Number(selectedTrade.status) === 2 && (
                  <div className="action-message success">
                    🎉 거래가 성공적으로 완료되었습니다!
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button 
              className="btn-secondary"
              onClick={() => window.open(
                `https://scan.worldland.foundation/address/0xaedf2a72540a7a6611ab80f365acfb7ef65d3a92`,
                '_blank'
              )}
            >
              🔍 블록체인에서 확인
            </button>
            <button className="btn-primary" onClick={() => setSelectedTrade(null)}>
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  }, [selectedTrade, getUserRole, isTradeExpired, getStatusInfo, getTradeFeeRate]);

  if (loading && userTrades.length === 0) {
    return (
      <div className="trade-list-container">
        <div className="loading-container">
          <div className="loading-spinner large"></div>
          <p>거래 목록을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trade-list-container">
      <div className="trade-list-header">
        <h2>💼 내 거래 목록</h2>
        <p>참여한 모든 거래를 확인하고 관리할 수 있습니다.</p>
      </div>

      <div className="trade-list-controls">
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            📋 전체 거래 ({userTrades.length})
          </button>
          <button 
            className={`filter-btn ${filter === 'seller' ? 'active' : ''}`}
            onClick={() => setFilter('seller')}
          >
            🔴 판매 거래 ({userTrades.filter(t => getUserRole(t) === 'seller').length})
          </button>
          <button 
            className={`filter-btn ${filter === 'buyer' ? 'active' : ''}`}
            onClick={() => setFilter('buyer')}
          >
            🔵 구매 거래 ({userTrades.filter(t => getUserRole(t) === 'buyer').length})
          </button>
        </div>

        <button 
          className="refresh-btn"
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? '🔄 새로고침 중...' : '🔄 새로고침'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          ❌ 거래 목록을 불러오는 중 오류가 발생했습니다: {error}
        </div>
      )}

      {filteredTrades.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>거래 내역이 없습니다</h3>
          <p>
            {filter === 'all' ? '아직 참여한 거래가 없습니다.' :
             filter === 'seller' ? '판매한 거래가 없습니다.' :
             '구매한 거래가 없습니다.'}
          </p>
          <button 
            className="create-trade-btn"
            onClick={() => {
              // 거래 생성 탭으로 이동하는 로직 추가 필요
              const event = new CustomEvent('switchTab', { detail: 'create' });
              window.dispatchEvent(event);
            }}
          >
            ➕ 새 거래 만들기
          </button>
        </div>
      ) : (
        <>
          {/* 액션 필요한 거래 요약 */}
          {(() => {
            const actionNeededTrades = filteredTrades.filter(trade => {
              const action = getNextAction(trade);
              return ['submit_proof', 'confirm_payment', 'cancel'].includes(action);
            });

            if (actionNeededTrades.length > 0) {
              return (
                <div className="action-needed-summary">
                  <h3>⚡ 액션이 필요한 거래 ({actionNeededTrades.length}개)</h3>
                  <p>아래 거래들에서 당신의 액션을 기다리고 있습니다.</p>
                </div>
              );
            }
            return null;
          })()}

          <div className="trades-grid">
            {filteredTrades.map((trade) => {
              const role = getUserRole(trade);
              const isExpired = isTradeExpired(trade);
              const action = getNextAction(trade);
              const needsAction = ['submit_proof', 'confirm_payment', 'cancel'].includes(action);
              
              // 해당 거래의 실제 수수료율 계산
              const tradeFeePercent = getTradeFeeRate(trade);
              const tradeFeePercentage = feePercentToPercentage(tradeFeePercent);

              console.log('내 주소:', account);
              console.log('거래 buyer:', trade.buyer);
              console.log('trade.status:', trade.status);
              console.log('만료?', isExpired, '만료일:', trade.expiresAt, '현재시각:', new Date()); 

              return (
                <div 
                  key={`${trade.tradeId}-${role}`} 
                  className={`trade-card ${needsAction ? 'needs-action' : ''} ${Number(trade.status) === 2 ? 'completed' : ''}`}
                  onClick={() => setSelectedTrade(trade)}
                >
                  <div className="trade-card-header">
                    <div className="trade-id">💼 거래 #{trade.tradeId}</div>
                    <div className="trade-role">
                      {role === 'seller' ? '🔴 판매' : '🔵 구매'}
                    </div>
                    {needsAction && (
                      <div className="action-indicator">⚡</div>
                    )}
                  </div>

                  <div className="trade-card-body">
                    <div className="trade-amount">
                      <span className="wlc-amount">{trade.wlcAmount} WLC</span>
                      <span className="arrow">↔</span>
                      <span className="krw-amount">
                        {parseInt(trade.krwAmount).toLocaleString()} 원
                      </span>
                    </div>
                    
                    <div className="trade-price">
                      개당 {Math.round(trade.krwAmount / trade.wlcAmount).toLocaleString()} 원
                    </div>

                    <div className="trade-status-area">
                      {getStatusBadge(trade.status)}
                      {isExpired && Number(trade.status) < 2 && (
                        <span className="expired-badge">⏰ 만료됨</span>
                      )}
                    </div>

                    {/* 액션 버튼 또는 상태 정보 */}
                    <div className="trade-action-area">
                      {renderActionButton(trade)}
                    </div>
                  </div>

                  <div className="trade-card-footer">
                    <div className="trade-date">
                      📅 {trade.createdAt.toLocaleDateString()}
                    </div>
                    <div className="view-details">
                      상세보기 →
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* 거래 상세 모달 */}
      {TradeDetailModal}

      {/* 송금 증명 제출 모달 */}
      <PaymentProofModal
        trade={paymentProofTrade}
        isOpen={showPaymentProof}
        onClose={() => {
          setShowPaymentProof(false);
          setPaymentProofTrade(null);
        }}
        onSuccess={() => {
          loadUserTrades(account); // 거래 목록 새로고침
        }}
      />
    </div>
  );
};

export default TradeList;